// ----------------------------------------------------------------------
// (c) Copyright 2020, Dark Star Systems, Inc.  All rights reserved.
// This file may contain proprietary and confidential information.
// DO NOT COPY or distribute in any form without prior written consent.
// ----------------------------------------------------------------------

import {Vector2, Vector3, Matrix4} from 'three'
import * as NGLViewer from 'ngl'
import {degToRad, radToDeg} from './utils'
import {userStore} from '@/store'
import type {MouseControllerMode} from '@/store/models'
import {defaultCameraParams} from '@/camera-rig'
import {watch} from '@vue/composition-api'

const tmpVector1 = new Vector3()
const tmpVector2 = new Vector3()
const tmpVector3 = new Vector3()
const tmpVector4 = new Vector3()

export type RotateSphericalCallback = (params: {theta: number; phi: number; dolly: number}) => void

// NGL callback types; see trackball-controls.ts
export type RotateCallback = (x: number, y: number) => void
export type PanCallback = (x: number, y: number) => void
export type ZoomCallback = (delta: number) => void

type MouseActionSet = [ string, NGLViewer.MouseActionCallback, string? ][]

// XXX use this?
/** Mouse action for adjusting scale of a component */
function scaleComponentDrag(stage: NGLViewer.Stage, dx: number, dy: number) {
  const component = stage.trackballControls.component
  if (!component)
    return

  const amount = (dx + dy) * 0.005
  const scale = component.scale.clone()
  // Assume the current scale is uniform. Add or subtract based on
  // the mouse movement, then clamp to prevent zero or negative
  // scales.
  scale.addScalar(amount)
  scale.clampScalar(0.01, Number.MAX_VALUE)

  component.scale = scale
  component.updateMatrix()
}


// XXX: how to use NGL.vue selectMolecule action? That needs a connection to NGL.vue

export class MouseControls {
  stage: NGLViewer.Stage
  freeCameraMouseControls: FreeCameraMouseControls
  selectComponent: (comp: NGLViewer.Component) => void

  private rotateScene: RotateCallback
  private rotateFreeCamera: RotateCallback
  private zoomFreeCamera: ZoomCallback

  private rotateSceneAction: NGLViewer.MouseActionCallback
  private rotateFreeCameraAction: NGLViewer.MouseActionCallback
  private zoomFreeCameraAction: NGLViewer.MouseActionCallback

  mouseActionsByMode: {[mode: string]: MouseActionSet}
  globalMouseActions: MouseActionSet

  constructor(stage: NGLViewer.Stage,
              freeCameraMouseControls: FreeCameraMouseControls,
              selectComponent: (comp: NGLViewer.Component) => void) {
    this.stage = stage
    this.freeCameraMouseControls = freeCameraMouseControls
    this.selectComponent = selectComponent.bind(this.stage)

    // Patch NGL's trackballControls.rotate with our version that uses the free camera rig
    // and save the original, which is used to rotate the scene (all the components).
    this.rotateScene = this.stage.trackballControls.rotate
    this.rotateSceneAction = (stage: NGLViewer.Stage, x: number, y: number) => this.rotateScene.bind(stage.trackballControls)(x, y)
    this.rotateFreeCamera = this.freeCameraMouseControls.cameraRotate.bind(this.freeCameraMouseControls)
    this.rotateFreeCameraAction = (_stage: NGLViewer.Stage, x: number, y: number) => this.rotateFreeCamera(x, y)
    this.zoomFreeCamera = this.freeCameraMouseControls.zoom.bind(this.freeCameraMouseControls)
    this.zoomFreeCameraAction = (_stage: NGLViewer.Stage, delta: number) => this.zoomFreeCamera(delta)

    // this.stage.trackballControls.rotate = this.rotateFreeCamera
    // this.stage.trackballControls.zoom = this.zoomFreeCamera
    // stage.trackballControls.panComponent = this.freeCameraMouseControls.panComponent.bind(this.freeCameraMouseControls)

    // also see NGL.vue scaleComponentDrag

    this.mouseActionsByMode = {
      view: [
        [ 'drag-left', this.rotateFreeCameraAction, 'orbit-view' ],
        [ 'scroll', this.zoomFreeCameraAction, 'zoom' ],

        [ 'ctrl-drag-left', this.rotateFreeCameraAction, 'rotate-scene' ] // all modes
      ],
      camera: [
      ],
      scene: [
        [ 'drag-left', this.rotateSceneAction, 'rotate-scene' ],
        [ 'drag-right', NGLViewer.MouseActions.panDrag, 'pan' ],
        [ 'scroll', this.zoomFreeCameraAction, 'zoom' ],

        [ 'ctrl-drag-left', this.rotateFreeCameraAction, 'orbit view' ],
        // [ 'ctrl-scroll', NGLViewer.MouseActions.zoomScroll ],
        // zRotateDrag somewhere?
      ],
      component: [
        [ 'drag-left', NGLViewer.MouseActions.rotateComponentDrag, 'rotate-component' ],
        [ 'drag-right', NGLViewer.MouseActions.panComponentDrag, 'pan-component' ],
        [ 'scroll', this.zoomFreeCameraAction, 'zoom' ],

        [ 'ctrl-drag-left', this.rotateFreeCameraAction, 'orbit view' ],
        // [ 'ctrl-scroll', NGLViewer.MouseActions.zoomScroll ],
      ]
    }

    // Used in every mode in addition to per-mode actions
    this.globalMouseActions = [
      [ 'clickPick-left', this.selectMolecule.bind(this) ],
      [ 'hoverPick', NGLViewer.MouseActions.tooltipPick ],
      // maybe add these?
      ['clickPick-right', NGLViewer.MouseActions.measurePick ],
      // ['clickPick-ctrl-left', NGLViewer.MouseActions.measurePick ],
      ['clickPick-middle', NGLViewer.MouseActions.movePick ], // moves clicked atom to origin by moving scene
    ]

    watch(() => userStore.profile?.ui.shotDesigner.mouseControllerMode || 'view',
          (newVal: MouseControllerMode|undefined, oldVal: MouseControllerMode|undefined) => {
            this.setMouseControlMode(newVal, oldVal)
          },
          {immediate: true})
  }

  buttonsToString(buttonsBitfield: number) {
    const buttons = []
    if (buttonsBitfield & 0x1)
      buttons.push('left')
    if (buttonsBitfield & 0x2)
      buttons.push('right')
    if (buttonsBitfield & 0x4)
      buttons.push('middle')
    return buttons.join(',')
  }

  keysToString(keysBitfield: number) {
    const keys = []
    if (keysBitfield & 0x1)
      keys.push('alt')
    if (keysBitfield & 0x2)
      keys.push('ctrl')
    if (keysBitfield & 0x4)
      keys.push('meta')
    if (keysBitfield & 0x8)
      keys.push('shift')
    return keys.join(',')
  }

  dumpMouseControls() {
    this.stage.mouseControls.actionList.forEach(act => {
      const func = act.callback?.name.toString() || act.callback?.toString() || '<none>'
      console.log(`action: ${act.type}+${this.buttonsToString(act.button)}+${this.keysToString(act.key)}, callback="${func}"`)
    })
  }

  setMouseControlMode(mode: MouseControllerMode|undefined, oldMode: MouseControllerMode|undefined) {
    // console.log(`MouseControls: mouse control mode changed from ${oldMode} to ${mode}`)
    this.stage.mouseControls.clear()
    if (mode !== undefined) {
      const actions = this.mouseActionsByMode[mode]
      actions.forEach(action => this.stage.mouseControls.add(action[0], action[1]))
    }
    this.globalMouseActions.forEach(action => this.stage.mouseControls.add(action[0], action[1]))

    this.dumpMouseControls()
  }

  selectMolecule(_stage: NGLViewer.Stage, pickingProxy: NGLViewer.PickingProxy): void {
    if (!pickingProxy)
      return
    const nglComp = pickingProxy.component as NGLViewer.StructureComponent
    this.selectComponent(nglComp)
  }
}

function allClose(v0: Vector3, v1: Vector3) {
  const epsilon = 1.0e-3
  return (Math.abs(v0.x - v1.x) < epsilon &&
    Math.abs(v0.y - v1.y) < epsilon &&
    Math.abs(v0.z - v1.z) < epsilon)
}

function allSmall(v0: Vector3) {
  const epsilon = 1.0e-3
  return (Math.abs(v0.x) < epsilon &&
    Math.abs(v0.y) < epsilon &&
    Math.abs(v0.z) < epsilon)
}

export class FreeCameraMouseControls {
  motionNotificationCallback: () => void | undefined

  constructor(callback: () => void | undefined) {
    this.cameraGoal.copy(this.cameraPos)
    this.motionNotificationCallback = callback
  }

  // called on each tick to update camera pose
  rotateCallback?: RotateSphericalCallback

  // Camera speed constants
  THETA_SPEED = 0.3 * Math.PI/180.0
  PHI_SPEED = 0.3 * Math.PI/180.0
  DOLLY_SPEED = 0.7

  cameraMass = 0.001
  cameraSpringiness = 1 // "k": spring constant
  cameraDamping = 2*Math.sqrt(this.cameraSpringiness * this.cameraMass) // critically damped

  // current position, velocity and goal; theta, phi, dolly
  // initial setup is done here
  cameraPos = new Vector3(degToRad(-145), degToRad(15), defaultCameraParams.cameraDistance)
  cameraVel = new Vector3()
  cameraGoal = new Vector3()

  /** damped simple harmonic motion
  // k: spring constant
  // m: mass
  // b: damping factor
  // outputs new velocity and position in v1 and x1. */
  updateShmStep(k: number, m: number, b: number, v0: Vector3, x0: Vector3, xgoal: Vector3, dt: number,
                v1: Vector3, x1: Vector3) {
    if (allSmall(v0) && allClose(x0, xgoal)) {
      v1.set(0, 0, 0)
      x1.copy(xgoal)
      return
    }
    // f = xgoal - x0
    v1.subVectors(xgoal, x0) // force
    // a = f*(k/m)
    v1.multiplyScalar(k / m) // acceleration
    // v1 = a * dt + v0 * (1-dt * b / m)
    v1.multiplyScalar(dt).add(v0).multiplyScalar(1 - dt * b / m)
    // x1 = dt * v1 + x0
    x1.copy(v1).multiplyScalar(dt).add(x0)
    //print("  update SHM step: dt=", dt, ", x0=", x0, ", goal=", xgoal, ", f=", f, ", vel=", v1)
  }

  // Large step sizes run into numerical stability issues, so break those
  // down into multiple steps. Ideally we'd compute max step size
  // dynamically based on dx or something, but this is fine for now.
  max_step_size_sec = 0.010
  updateShm(k: number, m: number, b: number, v0: Vector3, x0: Vector3, xgoal: Vector3, dt: number,
            v1: Vector3, x1: Vector3) {
    const nsteps = Math.floor(dt / this.max_step_size_sec) + 1
    for (let i = 0; i < nsteps; i++) {
      this.updateShmStep(k, m, b, v0, x0, xgoal, dt / nsteps, tmpVector1, tmpVector2)
      v0.copy(tmpVector1)
      x0.copy(tmpVector2)
    }
    v1.copy(tmpVector1)
    x1.copy(tmpVector2)
  }

  /** Call this on each frame tick, with deltaT = time elapsed since last call, in seconds */
  onTick(deltaT: number) {
    this.updateShm(this.cameraSpringiness, this.cameraMass, this.cameraDamping,
                   this.cameraVel, this.cameraPos, this.cameraGoal, deltaT,
                   tmpVector3, tmpVector4)
    this.cameraVel.copy(tmpVector3)
    this.cameraPos.copy(tmpVector4)
    const [theta, phi, dolly] = this.cameraPos.toArray()
    return {theta, phi, dolly}
  }

  // these are called with mouse movements, replacing ngl's trackballControls methods
  // of the same name & signature

  cameraRotate(x: number, y: number) {
    this.cameraGoal.x += x * this.THETA_SPEED // theta
    this.cameraGoal.y -= y * this.PHI_SPEED // phi
    this.motionNotificationCallback()
  }

  zoom(delta: number) {
    let dollySpeed = this.DOLLY_SPEED
    if (this.cameraGoal.z < 10)
      dollySpeed = this.DOLLY_SPEED * 0.5
    if (this.cameraGoal.z < 3)
      dollySpeed = this.DOLLY_SPEED * 0.1
    this.cameraGoal.z = Math.max(0.5, this.cameraGoal.z - delta * dollySpeed) // dolly
    this.motionNotificationCallback()
  }

  panComponent(dx: number, dy: number) {
    console.log(`Panning component by ${dx}, ${dy}`)
    this.motionNotificationCallback()
  }
}
